import { useCallback, useEffect, useRef, useState } from 'react';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'http://127.0.0.1:54321';
const EDGE_FN_URL = `${supabaseUrl}/functions/v1/admin-llm-spend`;

type Status = 'idle' | 'loading' | 'success' | 'error';

export interface SpendBucket {
  calls: number;
  cost_usd: number;
  error_count: number;
}

export interface SpendTotals {
  today: SpendBucket;
  last_7d: SpendBucket;
  last_30d: SpendBucket;
}

export interface SpendDailyRow {
  day: string;
  provider: string;
  model: string;
  call_count: number;
  success_count: number;
  error_count: number;
  fallback_count: number;
  total_prompt_tokens: number | null;
  total_completion_tokens: number | null;
  p50_latency_ms: number | null;
  p95_latency_ms: number | null;
  p99_latency_ms: number | null;
  cost_usd: number;
}

export interface SpendAlert {
  threshold_usd: number;
  today_exceeds_threshold: boolean;
}

export interface SpendHourlyRow {
  hour: string;
  provider: string;
  call_count: number;
  error_count: number;
}

interface DefaultResponse {
  totals: SpendTotals;
  daily: SpendDailyRow[];
  alert: SpendAlert;
}

interface HourlyResponse {
  hourly: SpendHourlyRow[];
}

interface UseAdminLlmSpendResult {
  status: Status;
  totals: SpendTotals | null;
  daily: SpendDailyRow[];
  alert: SpendAlert | null;
  hourly: SpendHourlyRow[];
  error: string | null;
  lastFetchedAt: Date | null;
  refetch: () => void;
}

/**
 * Fetches LLM spend data from the password-gated admin-llm-spend edge
 * function. Mirrors useAdminLlmRuns — auto-refreshes every 60 seconds while
 * visible. Daily totals are the primary signal; hourly is fetched in parallel
 * and fails silently so the page still renders if the rate-limit view errors.
 */
export function useAdminLlmSpend(password: string): UseAdminLlmSpendResult {
  const [status, setStatus] = useState<Status>('idle');
  const [totals, setTotals] = useState<SpendTotals | null>(null);
  const [daily, setDaily] = useState<SpendDailyRow[]>([]);
  const [alert, setAlert] = useState<SpendAlert | null>(null);
  const [hourly, setHourly] = useState<SpendHourlyRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const inFlight = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!password || inFlight.current) return;
    inFlight.current = true;
    setStatus('loading');
    try {
      const headers = { 'X-Admin-Password': password };
      // Hourly is best-effort — never fail the page on it.
      const hourlyUrl = new URL(EDGE_FN_URL);
      hourlyUrl.searchParams.set('range', 'hourly');

      const [defaultRes, hourlyRes] = await Promise.all([
        fetch(EDGE_FN_URL, { method: 'GET', headers }),
        fetch(hourlyUrl.toString(), { method: 'GET', headers }).catch(
          () => null,
        ),
      ]);

      if (!defaultRes.ok) {
        const body = await defaultRes
          .json()
          .catch(() => ({ error: `HTTP ${defaultRes.status}` }));
        throw new Error(body.error ?? `HTTP ${defaultRes.status}`);
      }
      const defaultJson: DefaultResponse = await defaultRes.json();
      setTotals(defaultJson.totals);
      setDaily(defaultJson.daily);
      setAlert(defaultJson.alert);

      if (hourlyRes && hourlyRes.ok) {
        const hourlyJson: HourlyResponse = await hourlyRes.json();
        setHourly(hourlyJson.hourly);
      } else {
        // best-effort: leave hourly empty on transient failure
        setHourly([]);
      }

      setLastFetchedAt(new Date());
      setStatus('success');
      setError(null);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      inFlight.current = false;
    }
  }, [password]);

  useEffect(() => {
    fetchOnce();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchOnce();
    }, 60_000);
    return () => clearInterval(interval);
  }, [fetchOnce]);

  return {
    status,
    totals,
    daily,
    alert,
    hourly,
    error,
    lastFetchedAt,
    refetch: fetchOnce,
  };
}
